import { Module } from "vuex";

import { ChatApi, ChatCreateRequest } from "@/api/chat";
import { RootState } from "@/types";

import { ChatMemberDto } from "./interfaces/chatMember";

export interface ChatState {
  token: string | undefined;
  members: ChatMemberDto[];
  unreadChannels: Record<string, number>;
  selectedChannel: string;
}

export const chat: Module<ChatState, RootState> = {
  namespaced: true,
  state: {
    token: undefined,
    members: [],
    unreadChannels: {},
    selectedChannel: "",
  },
  mutations: {
    setToken(state: ChatState, token: string): void {
      state.token = token;
    },
    addMember(state: ChatState, member: ChatMemberDto): void {
      state.members.push(member);
    },
    setUnreadChannels(state: ChatState, unreadChannels: Record<string, number>): void {
      state.unreadChannels = { ...state.unreadChannels, ...unreadChannels };
    },
    setSelectedChannel(state: ChatState, sid: string): void {
      state.selectedChannel = sid;
    },
  },
  actions: {
    async connect({ commit }): Promise<void> {
      const token = await ChatApi.access();
      commit("setToken", token);
    },
    async getMember({ state, commit }, id: string): Promise<void> {
      const foundMember = state.members.find((member) => member.uid === id);
      const member = foundMember || (await ChatApi.chatMember(id));
      commit("addMember", member);
    },
    async createChat({ state, commit }, request: ChatCreateRequest): Promise<void> {
      await ChatApi.create(request);
    },
  },
  getters: {
    token: (state: ChatState): string | undefined => state.token,
    memberId: (state: ChatState, getters, rootState: RootState, rootGetters): string | undefined => rootGetters.uid,
    participants: (state: ChatState) =>
      state.members.reduce((accumulator: Record<string, any>, currentValue) => {
        accumulator[currentValue.uid] = {
          displayName: currentValue.displayName,
          photoURL: currentValue.photoURL,
          uid: currentValue.uid,
        };
        return accumulator;
      }, {}),
    unreadMessagesCount: (state: ChatState) =>
      Object.values(state.unreadChannels).reduce((a, e) => [(a += e), a][1], 0),
    selectedChannel: (state: ChatState) => state.selectedChannel,
  },
};
